// Report page /jobs/:id (DESIGN_SPEC §11.4, RESTYLE §3–4). The variant follows the EFFECTIVE state from the event stream,
// never the record: queued view · live cockpit · document (completed, or cancelled after dispatch: partial) · failed ·
// cancelled before dispatch. live → document swaps in one 180 ms cross-fade (View Transition).
//   <JobDetailPage />   route "report"; sets document.title ("● name — running · malbox" → "name — malicious 90/100 · malbox")
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import moth72 from "@/assets/specimen/moth-72.json";
import { useReportKeys } from "@/components/shell/pageKeys";
import { Alert, Arrow, Ascii, Empty, Kicker, Loading, Plate, Specimen, buttonClass } from "@/components/ui";
import { isMissingJob, useJob, useJobs, useNodes } from "@/hooks";
import { isApiError } from "@/lib/api";
import { viewTransition } from "@/lib/motion";
import type { ReportCtx } from "@/lib/derive/types";
import { jobName } from "@/lib/records";
import type { Job } from "@/types";

import { WaitClockLens } from "@/features/report/ClockLens";
import { deriveFacts } from "@/features/report/facts";
import { planDocument, type DocumentPlan } from "@/features/report/figOrder";
import { LiveCockpit } from "@/features/report/LiveCockpit";
import { QueuedView } from "@/features/report/QueuedView";
import { ReportBody } from "@/features/report/ReportBody";
import { DocumentActions, ReportHeader } from "@/features/report/ReportHeader";
import { FailedActions, TerminalBody, TerminalLens } from "@/features/report/TerminalViews";
import { useReportCtx, type Variant } from "@/features/report/useReportCtx";
import { VerdictLens } from "@/features/report/VerdictLens";

const FAILED_ONLY = ["thirdparty", "config", "static", "artifacts", "events", "network", "indicators", "session"] as const;
const BEFORE_DISPATCH_ONLY = ["thirdparty", "static"] as const;

function useTitle(job: Job, ctx: ReportCtx, variant: Variant) {
  const name = jobName(job);
  const v = ctx.stream.verdict;
  const state = ctx.stream.state;
  const title =
    variant === "live"
      ? `● ${name} — running · malbox`
      : variant === "queued"
        ? `${name} — queued · malbox`
        : state === "completed"
          ? v
            ? `${name} — ${v.label} ${v.score}/100 · malbox`
            : `${name} — completed · malbox`
          : `${name} — ${state}${v && variant === "document" ? ` · ${v.label} ${v.score}/100` : ""} · malbox`;
  useEffect(() => {
    document.title = title;
  }, [title]);
}

/** Hold the shown variant; the live → document flip runs as one view transition (a root cross-fade). */
function useShownVariant(variant: Variant): Variant {
  const [shown, setShown] = useState(variant);
  useEffect(() => {
    if (variant === shown) return;
    if (shown === "live" && variant === "document") viewTransition(() => setShown(variant));
    else setShown(variant);
  }, [variant, shown]);
  return shown;
}

function Report({ job }: { job: Job }) {
  const data = useReportCtx(job);
  const variant = useShownVariant(data.variant);
  const ctx = useMemo<ReportCtx>(() => {
    const mode = variant === "live" ? "live" : "document";
    return mode === data.ctx.mode ? data.ctx : { ...data.ctx, mode };
  }, [data.ctx, variant]);
  const { stream } = ctx;
  const queued = variant === "queued";
  const { data: jobs } = useJobs(100, { enabled: queued, refetchMs: queued ? 5000 : false });
  const { data: nodes } = useNodes({ enabled: queued });

  const facts = useMemo(
    () =>
      deriveFacts({
        job,
        events: stream.snap.events,
        state: stream.state,
        transitions: stream.transitions,
        t0: stream.t0,
        t0Source: stream.t0Source,
        verdict: stream.verdict,
        tags: data.tags,
        noise: ctx.noise,
        jobs: queued ? jobs : undefined,
        nodes: queued ? nodes : undefined,
      }),
    [job, stream, data.tags, ctx.noise, queued, jobs, nodes],
  );

  const plan = useMemo<DocumentPlan | null>(() => {
    if (variant === "document") return planDocument(ctx);
    if (variant === "failed") return planDocument(ctx, { only: FAILED_ONLY });
    if (variant === "cancelled-before") return planDocument(ctx, { only: BEFORE_DISPATCH_ONLY });
    return null;
  }, [ctx, variant]);

  useTitle(job, ctx, variant);
  useReportKeys(variant);

  if (variant === "live")
    return (
      <article aria-label={`Live analysis of ${jobName(job)}`}>
        <LiveCockpit ctx={ctx} facts={facts} />
      </article>
    );

  let lens;
  let actions;
  if (variant === "document") {
    lens = <VerdictLens ctx={ctx} facts={facts} tags={data.tags} partial={stream.state !== "completed"} />;
    actions = <DocumentActions ctx={ctx} plan={plan ?? undefined} />;
  } else if (variant === "queued") {
    lens = <WaitClockLens job={job} facts={facts} />;
  } else {
    lens = <TerminalLens ctx={ctx} facts={facts} />;
    actions = variant === "failed" ? <FailedActions ctx={ctx} /> : undefined;
  }

  return (
    <article aria-label={`Analysis of ${jobName(job)}`}>
      <div className="page pt-8">
        <ReportHeader ctx={ctx} variant={variant} facts={facts} lens={lens} actions={actions} identity={variant !== "queued"} />
      </div>
      {variant === "document" && plan && <ReportBody ctx={ctx} plan={plan} />}
      {variant === "queued" && <QueuedView ctx={ctx} />}
      {(variant === "failed" || variant === "cancelled-before") && plan && <TerminalBody ctx={ctx} plan={plan} facts={facts} />}
    </article>
  );
}

function Missing({ id }: { id: string }) {
  useEffect(() => {
    document.title = "Analysis not found · malbox";
  }, []);
  return (
    <div className="page grid min-h-[calc(100svh-var(--nav-h)-200px)] place-items-center">
      <Empty
        figure={
          <>
            <Specimen label="An empty specimen plate" className="mb-3 w-72 max-w-full">
              <Plate sm>
                <Ascii art={moth72} maxPx={4.6} />
              </Plate>
            </Specimen>
            <Kicker as="div">Analysis · 404</Kicker>
          </>
        }
        action={
          <Link className={`${buttonClass({ variant: "secondary" })} mt-2`} to="/jobs" viewTransition>
            All analyses <Arrow />
          </Link>
        }
      >
        No analysis is pinned at{" "}
        <span className="id whitespace-normal wrap-anywhere" translate="no">
          {id}
        </span>
        .
      </Empty>
    </div>
  );
}

export function JobDetailPage() {
  const { id = "" } = useParams();
  const q = useJob(id);
  if (q.data) return <Report key={id} job={q.data} />;
  if (q.error) {
    if (isMissingJob(q.error)) return <Missing id={id} />;
    const e = q.error;
    return (
      <div className="page pt-10">
        <Alert
          head="Couldn't load this analysis"
          actions={
            <button type="button" className="lnk" onClick={() => void q.refetch()}>
              Retry now
            </button>
          }
        >
          {isApiError(e) ? `${e.status} from ${e.path}${e.detail ? `: ${e.detail}` : ""}` : e instanceof Error ? e.message : String(e)}
        </Alert>
      </div>
    );
  }
  return (
    <div className="page pt-10">
      <Loading verb="Loading">the analysis…</Loading>
    </div>
  );
}
